import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ClipboardText, CheckCircle, XCircle, ArrowRight, UserSwitch } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { SectionHeader } from "../components/shared";
import { Button } from "../components/ui/button";
import { ReassignApproverDialog } from "../components/ReassignApproverDialog";

export default function MyApprovals() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [declineFor, setDeclineFor] = useState(null);
  const [reason, setReason] = useState("");
  const [reassignFor, setReassignFor] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["my-approvals"],
    queryFn: async () => (await api.get("/my-approvals")).data,
  });
  const items = data?.items || [];

  const decide = useMutation({
    mutationFn: async ({ id, decision, reason }) => (await api.post(`/purchase-orders/${id}/approval-decision`, { decision, reason: reason || "" })).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["my-approvals"] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      qc.invalidateQueries({ queryKey: ["approval-stats"] });
      setDeclineFor(null); setReason("");
      toast.success(r.decision === "approve" ? "Approved" : "Declined");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <div className="animate-fade-up" data-testid="my-approvals-page">
      <SectionHeader title="My Approvals" subtitle={`${items.length} purchase order${items.length === 1 ? "" : "s"} awaiting your approval`} />

      {isLoading ? (
        <div className="flex items-center justify-center py-20"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="border border-border bg-card p-10 text-center rounded-sm" data-testid="my-approvals-empty">
          <ClipboardText size={40} className="mx-auto text-muted-foreground mb-3" />
          <h2 className="text-lg font-bold">All caught up</h2>
          <p className="text-sm text-muted-foreground mt-1">No purchase orders are waiting on your approval right now.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((po) => (
            <div key={po.id} className="border border-border bg-card rounded-sm p-4" data-testid="my-approval-row">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => navigate(`/purchasing/doc/${po.id}`)} className="font-black text-lg hover:text-primary" data-testid="my-approval-open">{po.doc_no}</button>
                    <span className="text-sm text-muted-foreground">· {po.vendor_name}</span>
                    {po.department && <span className="text-[10px] uppercase font-bold text-primary border border-primary/30 bg-primary/10 px-1.5 py-0.5 rounded-sm">{po.department}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Submitted by {po.approval_submitted_by || "—"}{po.approval_submitted_at ? ` · ${String(po.approval_submitted_at).slice(0, 10)}` : ""}
                    {po.sales_order_no ? ` · SO ${po.sales_order_no}` : ""}
                    <span data-testid="my-approval-approver">{` · Approver: ${po.approval_approver_name || "Unassigned"}`}</span>
                  </p>
                </div>
                <div className="text-right">
                  <div className="font-bold text-primary tabular">{money(po.total)}</div>
                </div>
              </div>

              {declineFor === po.id ? (
                <div className="mt-3 space-y-2">
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason for declining (optional)" className="w-full rounded-sm border border-border p-2 text-sm outline-none focus:border-primary resize-none" data-testid="my-approval-reason" />
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => { setDeclineFor(null); setReason(""); }} className="rounded-sm h-8 text-xs" data-testid="my-approval-decline-cancel">Cancel</Button>
                    <Button onClick={() => decide.mutate({ id: po.id, decision: "decline", reason })} disabled={decide.isPending} className="rounded-sm h-8 text-xs bg-rose-600 hover:bg-rose-700 text-white" data-testid="my-approval-decline-confirm">Confirm Decline</Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 justify-end mt-3">
                  <Button onClick={() => navigate(`/purchasing/doc/${po.id}`)} variant="outline" className="rounded-sm h-8 text-xs" data-testid="my-approval-view">View <ArrowRight size={14} className="ml-1" /></Button>
                  <Button onClick={() => setReassignFor(po)} variant="outline" className="rounded-sm h-8 text-xs" data-testid="my-approval-reassign"><UserSwitch size={15} weight="bold" className="mr-1" /> Reassign</Button>
                  <Button onClick={() => setDeclineFor(po.id)} variant="outline" className="rounded-sm h-8 text-xs border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="my-approval-decline"><XCircle size={15} weight="bold" className="mr-1" /> Decline</Button>
                  <Button onClick={() => decide.mutate({ id: po.id, decision: "approve" })} disabled={decide.isPending} className="rounded-sm h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="my-approval-approve"><CheckCircle size={15} weight="bold" className="mr-1" /> Approve</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <ReassignApproverDialog po={reassignFor} onClose={() => setReassignFor(null)} />
    </div>
  );
}
